import {memo, useCallback, useEffect, useRef, useState} from 'react';
import type {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import {
    borderRadius,
    colors,
    fontSize,
    fontWeight,
    gradients,
    letterSpacing,
    lineHeight,
    overlay,
    shadows,
    spacing,
    transition,
    typography
} from '@/styles/tokens';
import {iconTileSx} from '@/styles/iconTile';
import {COUNT_UP_DURATION_MS, useCountUp} from '@/hooks/useCountUp';
import {useInView} from '@/hooks/useInView';

/** A labelled meter in the card's stat row, e.g. "Predictability — 95%". */
export interface PricingModelMetric {
    label: string;
    /** 0–100. Drives both the printed number and the bar width. */
    value: number;
    /** Bar colour. Defaults to the navy used for neutral metrics. */
    color?: string;
}

/** One of the two tinted lists (advantages / limitations). */
export interface PricingModelList {
    title: string;
    items: string[];
    /** `positive` renders the green tint, `caution` the orange one. Defaults to `positive`. */
    tone?: 'positive' | 'caution';
}

export interface PricingModelDetailCard {
    /** Icon element for the tile. Attached on the page so data files stay JSX-free. */
    icon?: ReactNode;
    /** Model name, e.g. "Fixed Price". */
    title: string;
    /** One-line positioning under the title. */
    tagline?: string;
    /** Label/value rows in the dark panel — starting price, duration, billing basis. */
    facts?: {label: string; value: string}[];
    /** Body copy for the light panel. */
    paragraphs: string[];
    metrics?: PricingModelMetric[];
    lists?: PricingModelList[];
    /** Pill row under "Best suited to". */
    bestSuitedLabel?: string;
    bestSuitedFor?: string[];
    ctaLabel?: string;
}

export interface PricingModelDetailCardsProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    cards: PricingModelDetailCard[];
    onCtaClick?: () => void;
}

// ─── Tabs ────────────────────────────────────────────────────────────────────
// One model at a time: four full cards stacked made the section very long, and the
// models are alternatives, so a tab row matches the decision being made. Same pill
// treatment as `HomeDecisionMakers`, the page's other tabbed section.
const tabsRowSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    gap: spacing[12],
    mb: {xs: spacing[24], md: spacing[32]}
} as const;

const getTabSx = (isActive: boolean) =>
    ({
        px: {xs: spacing[16], sm: spacing[24]},
        py: {xs: spacing[8], sm: spacing[12]},
        borderRadius: borderRadius.full,
        border: `1px solid ${colors.primary.deep}`,
        background: isActive ? colors.primary.deep : colors.background.default,
        cursor: 'pointer',
        transition: `background ${transition.medium}, color ${transition.medium}`,
        userSelect: 'none' as const,
        '&:hover': {background: isActive ? colors.primary.deep : colors.background.tag}
    } as const);

const getTabLabelSx = (isActive: boolean) =>
    ({
        fontSize: {xs: fontSize[14], sm: fontSize[16]},
        fontWeight: isActive ? fontWeight.bold : fontWeight.medium,
        color: isActive ? colors.text.inverse : colors.text.primary,
        lineHeight: lineHeight[24],
        whiteSpace: 'nowrap' as const
    } as const);

// ─── Card shell ──────────────────────────────────────────────────────────────
// Two panels side by side from `md` up (dark summary rail left, detail right) and
// stacked below it. One plain grid of Boxes rather than a MUI Grid: every card is a
// single row, so the extra nesting would buy nothing.

const cardSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '300px 1fr', lg: '340px 1fr'},
    overflow: 'hidden',
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius['3xl'],
    boxShadow: shadows.lg
} as const;

// ─── Dark summary panel ──────────────────────────────────────────────────────
const summarySx = {
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[16],
    background: gradients.navyPanelSoft,
    p: {xs: spacing[24], md: spacing[32]},
    // The decorative circle bleeds off the panel's top-right corner.
    overflow: 'hidden'
} as const;

const summaryOrbSx = {
    position: 'absolute' as const,
    top: -60,
    right: -60,
    width: 180,
    height: 180,
    borderRadius: borderRadius.circle,
    bgcolor: overlay.whiteSubtle,
    pointerEvents: 'none' as const
} as const;

// A second, larger orb anchored behind the CTA at the panel's bottom-left, mirroring
// the top-right one so the panel is framed rather than weighted to one corner.
const summaryOrbLowSx = {
    position: 'absolute' as const,
    bottom: -70,
    left: -70,
    width: 200,
    height: 200,
    borderRadius: borderRadius.circle,
    bgcolor: overlay.whiteSubtle,
    pointerEvents: 'none' as const
} as const;

// Everything above the fact rows sits in this stack so the rows can be pinned to
// the panel's bottom with `mt: auto`, keeping the CTA on one baseline per card.
const summaryTopSx = {position: 'relative' as const, display: 'flex', flexDirection: 'column' as const, gap: spacing[16]} as const;

// The shared tile already carries the blue gradient, which would disappear against
// the navy panel — a translucent white square reads correctly here instead.
const summaryIconSx = {
    ...iconTileSx,
    background: overlay.whiteFaint,
    border: `1px solid ${overlay.whiteLight}`
} as const;

const summaryTitleSx = {...typography.cardTitle, color: colors.text.inverse} as const;

const summaryTaglineSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular,
    color: overlay.whiteSubtitle,
    lineHeight: lineHeight[20]
} as const;

const factsSx = {position: 'relative' as const, mt: {xs: spacing[16], md: 'auto'}, pt: spacing[16]} as const;

const factRowSx = {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing[12],
    py: spacing[12],
    borderTop: `1px solid ${overlay.whiteLight}`
} as const;

const factLabelSx = {
    fontSize: fontSize[10],
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: overlay.whiteDim,
    lineHeight: lineHeight[20]
} as const;

const factValueSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    color: colors.text.inverse,
    lineHeight: lineHeight[20],
    textAlign: 'right' as const
} as const;

// Inverted CTA: on the navy panel the primary navy button would vanish, so this is
// a translucent white surface that fills in on hover.
const ctaSx = {
    position: 'relative' as const,
    mt: spacing[8],
    textTransform: 'none' as const,
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    color: colors.text.inverse,
    bgcolor: overlay.whiteFaint,
    border: `1px solid ${overlay.whiteBorder}`,
    borderRadius: borderRadius.full,
    px: spacing[24],
    py: spacing[12],
    '&:hover': {bgcolor: colors.text.inverse, color: colors.primary.main, borderColor: colors.text.inverse}
} as const;

// ─── Light detail panel ──────────────────────────────────────────────────────
const detailSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[20],
    p: {xs: spacing[24], md: spacing[32]}
} as const;

const paragraphSx = {
    fontSize: fontSize[15],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const metricsRowSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
    gap: spacing[16]
} as const;

const metricBoxSx = {
    bgcolor: colors.background.subtle,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    p: spacing[16]
} as const;

const metricTopSx = {display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: spacing[8], mb: spacing[12]} as const;

const metricLabelSx = {
    fontSize: fontSize[10],
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.text.secondary,
    lineHeight: lineHeight[20]
} as const;

const metricValueSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const metricUnitSx = {fontSize: fontSize[10], fontWeight: fontWeight.medium, color: colors.text.secondary} as const;

const metricTrackSx = {height: 6, borderRadius: borderRadius.full, bgcolor: colors.neutral.grayLight, overflow: 'hidden'} as const;

// No CSS transition: the width is read straight off the counting value, so the meter
// and its number are driven by one animation. `useCountUp` honours reduced-motion.
const metricFillSx = (metric: PricingModelMetric, shown: number) =>
    ({
        width: `${shown}%`,
        height: '100%',
        borderRadius: borderRadius.full,
        bgcolor: metric.color ?? colors.primary.main
    } as const);

const listsRowSx = {display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: spacing[16]} as const;

// Tone drives surface, border and bullet colour together, so a list is never a green
// box with orange dots.
const listTones = {
    positive: {bg: colors.background.successTint, border: colors.status.successBorder, dot: colors.accent.emeraldDark},
    caution: {bg: colors.background.orangeTint, border: colors.accent.orangeTintBorder, dot: colors.accent.orangeMid}
} as const;

const listTitleSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[20],
    mb: spacing[12]
} as const;

const listItemSx = {display: 'flex', alignItems: 'flex-start', gap: spacing[8], '&:not(:last-of-type)': {mb: spacing[8]}} as const;

const listDotSx = {width: 5, height: 5, borderRadius: borderRadius.circle, mt: spacing[8], flexShrink: 0} as const;

const listTextSx = {
    fontSize: fontSize[13],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

const bestSuitedLabelSx = {
    fontSize: fontSize[10],
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase' as const,
    color: colors.primary.main,
    lineHeight: lineHeight[20],
    mb: spacing[12]
} as const;

const pillsRowSx = {display: 'flex', flexWrap: 'wrap' as const, gap: spacing[8]} as const;

const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing[8],
    bgcolor: colors.background.tag,
    border: `1px solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.full,
    px: spacing[12],
    py: spacing[8],
    fontSize: fontSize[12],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[20]
} as const;

const pillDotSx = {width: 5, height: 5, borderRadius: borderRadius.circle, bgcolor: colors.primary.main, flexShrink: 0} as const;

/**
 * One labelled meter whose figure counts up as the bar fills. Its own component
 * because `useCountUp` is a hook and so cannot run inside a `map`.
 *
 * `run` counts triggers: 0 is the resting state, which shows the real figure so a panel
 * never looks half-drawn before anything fires, and every increment replays the count.
 */
const MetricMeter = ({metric, run}: {metric: PricingModelMetric; run: number}) => {
    const animating = run > 0;
    // Two decimals so the bar's width moves smoothly; the printed figure is rounded.
    const counted = useCountUp(metric.value, 2, animating, COUNT_UP_DURATION_MS, run);
    const shown = animating ? counted : metric.value;

    return (
        <Box sx={metricBoxSx}>
            <Box sx={metricTopSx}>
                <Typography component="span" className="font-regular-text" sx={metricLabelSx}>
                    {metric.label}
                </Typography>
                <Typography component="span" className="font-headers" sx={metricValueSx}>
                    {Math.round(shown)}
                    <Box component="span" sx={metricUnitSx}>
                        %
                    </Box>
                </Typography>
            </Box>
            {/* A meter, not a progress bar: the value is a rating of the model, not a task's completion.
                `aria-valuenow` carries the real figure, never the animating one. */}
            <Box
                sx={metricTrackSx}
                role="meter"
                aria-label={metric.label}
                aria-valuenow={metric.value}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <Box sx={metricFillSx(metric, shown)} />
            </Box>
        </Box>
    );
};

const ctaIcon = <ArrowForwardRoundedIcon fontSize="small" />;

const DEFAULT_BEST_SUITED_LABEL = 'Best suited to';

/**
 * PricingModelDetailCards — full-width, one-per-row pricing model cards: a dark
 * summary rail (numbered badge, icon, model name, starting price / duration /
 * billing facts and a CTA) beside a light detail panel (narrative copy, three
 * metric meters, advantages vs limitations lists, and a "best suited to" pill row).
 *
 * Distinct from `RateCardGrid` (per-resource rate cards in a scroll track) and
 * `PricingSection` (SaaS tier columns) — a different card anatomy, not a variant.
 */
const PricingModelDetailCards = memo(function PricingModelDetailCards({
    eyebrow,
    heading,
    subheading,
    cards,
    onCtaClick
}: PricingModelDetailCardsProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    // No hover trigger here: the panel is large and read at length, so replaying it under
    // the pointer would fight the reader. Scrolling the cards into view plays the meters,
    // and opening another tab plays that panel's. At rest (`run` 0) every meter shows its
    // real figure, so a panel that has not been triggered still reads correctly.
    const [setInViewRef, inView] = useInView({triggerOnce: true, threshold: 0.2});
    const [run, setRun] = useState(0);
    const replay = useCallback(() => setRun((current) => current + 1), []);

    // The observer fires once; this arms the first play and stands down if opening a tab
    // already started the run.
    const played = useRef(false);
    useEffect(() => {
        if (!inView || played.current) return;
        played.current = true;
        setRun((current) => (current === 0 ? 1 : current));
    }, [inView]);

    const selectTab = (index: number) => {
        setActiveIndex(index);
        replay();
    };

    return (
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading} />

            <Box ref={setInViewRef} sx={tabsRowSx} role="tablist" aria-label={typeof heading === 'string' ? heading : 'Pricing models'}>
                {cards.map((card, index) => (
                    <Box
                        key={card.title}
                        component="button"
                        type="button"
                        role="tab"
                        id={`pricing-model-tab-${index}`}
                        aria-selected={activeIndex === index}
                        aria-controls={`pricing-model-panel-${index}`}
                        onClick={() => selectTab(index)}
                        sx={getTabSx(activeIndex === index)}
                    >
                        <Typography component="span" className="font-headers" sx={getTabLabelSx(activeIndex === index)}>
                            {card.title}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Every panel is rendered so all four models are in the server HTML for
                crawlers; only the selected one is shown. */}
            {cards.map((card, index) => (
                <Box
                    key={card.title}
                    role="tabpanel"
                    id={`pricing-model-panel-${index}`}
                    aria-labelledby={`pricing-model-tab-${index}`}
                    hidden={activeIndex !== index}
                >
                    <Box sx={cardSx}>
                        <Box sx={summarySx}>
                            <Box sx={summaryOrbSx} aria-hidden="true" />
                            <Box sx={summaryOrbLowSx} aria-hidden="true" />

                            <Box sx={summaryTopSx}>
                                {card.icon && <Box sx={summaryIconSx}>{card.icon}</Box>}

                                <Box>
                                    <Typography component="h3" className="font-headers" sx={summaryTitleSx}>
                                        {card.title}
                                    </Typography>
                                    {card.tagline && (
                                        <Typography component="p" className="font-regular-text" sx={summaryTaglineSx}>
                                            {card.tagline}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {card.facts && card.facts.length > 0 && (
                                <Box sx={factsSx}>
                                    {card.facts.map((fact) => (
                                        <Box key={fact.label} sx={factRowSx}>
                                            <Typography component="span" className="font-regular-text" sx={factLabelSx}>
                                                {fact.label}
                                            </Typography>
                                            <Typography component="span" className="font-headers" sx={factValueSx}>
                                                {fact.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {card.ctaLabel && (
                                <MUIButton
                                    label={card.ctaLabel}
                                    aria-label={`${card.ctaLabel} — ${card.title}`}
                                    variant="text"
                                    endIcon={ctaIcon}
                                    onClick={onCtaClick}
                                    sx={ctaSx}
                                />
                            )}
                        </Box>

                        <Box sx={detailSx}>
                            {card.paragraphs.map((paragraph) => (
                                <Typography key={paragraph} component="p" className="font-regular-text" sx={paragraphSx}>
                                    {paragraph}
                                </Typography>
                            ))}

                            {card.metrics && card.metrics.length > 0 && (
                                <Box sx={metricsRowSx}>
                                    {card.metrics.map((metric) => (
                                        <MetricMeter key={metric.label} metric={metric} run={activeIndex === index ? run : 0} />
                                    ))}
                                </Box>
                            )}

                            {card.lists && card.lists.length > 0 && (
                                <Box sx={listsRowSx}>
                                    {card.lists.map((list) => {
                                        const tone = listTones[list.tone ?? 'positive'];
                                        return (
                                            <Box
                                                key={list.title}
                                                sx={{
                                                    bgcolor: tone.bg,
                                                    border: `1px solid ${tone.border}`,
                                                    borderRadius: borderRadius.lg,
                                                    p: spacing[16]
                                                }}
                                            >
                                                <Typography component="h4" className="font-headers" sx={listTitleSx}>
                                                    {list.title}
                                                </Typography>
                                                {list.items.map((item) => (
                                                    <Box key={item} sx={listItemSx}>
                                                        <Box sx={{...listDotSx, bgcolor: tone.dot}} />
                                                        <Typography component="p" className="font-regular-text" sx={listTextSx}>
                                                            {item}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {card.bestSuitedFor && card.bestSuitedFor.length > 0 && (
                                <Box>
                                    <Typography component="p" className="font-regular-text" sx={bestSuitedLabelSx}>
                                        {card.bestSuitedLabel ?? DEFAULT_BEST_SUITED_LABEL}
                                    </Typography>
                                    <Box sx={pillsRowSx}>
                                        {card.bestSuitedFor.map((item) => (
                                            <Typography key={item} component="span" className="font-regular-text" sx={pillSx}>
                                                <Box component="span" sx={pillDotSx} />
                                                {item}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            ))}
        </MUIContainer>
    );
});

export default PricingModelDetailCards;
